import { get, urlReg } from '@chat/utils';
import type { DifyLogData } from '@chat/components';
import { FAQ_WORKFLOW_LOG } from '@/api/url';
import { abortWorkflowSSERequest } from '@/utils/WorkflowSSEUtil';
import type { DifyServerImp } from '@/typings/global';

type DifyRemoteImageFile = {
    type: 'image';
    transfer_method: 'remote_url';
    url: string;
};

type FacePlaceholderResult = {
    text: string;
    replacements: Record<string, string>;
};

/** Dify 工作流及其消息栈管理服务。 */
class DifyServer implements DifyServerImp {
    private static readonly IMAGE_TAG_REG = /<img[^>]*>/gi;

    private static readonly DIFY_FILE_URL_PREFIX = 'https://fs.img4399.com/kf/';

    private static readonly DIFY_PUBLIC_URL_PREFIX = 'https://kf.img4399.com/';

    private pendingMsgStack: string[] = [];

    private activeMsgStack = '';

    private sopHit: 0 | 1 | undefined;

    private getRemoteImageUrlFromTag = (tag: string): string => {
        if (!tag || typeof tag !== 'string') return '';
        const srcMatch = tag.match(/\ssrc=(['"])(.*?)\1/i);
        const rawUrl = srcMatch?.[2] || '';
        const url = rawUrl === 'src' ? '' : rawUrl.trim();
        return /^https?:\/\//i.test(url) ? url : '';
    };

    /** 将客服表情编码转换为 Dify 可理解的占位符，并返回原始表情映射。 */
    normalizeFacePlaceholders = (text: string): FacePlaceholderResult => {
        if (!text || typeof text !== 'string') {
            return { text, replacements: {} };
        }

        let faceIndex = 0;
        const replacements: Record<string, string> = {};
        const result = text.replace(
            /\[face=(\d+);([^;\]]+);([^\]]+)\]/gi,
            (origin, _id, _uri, name) => {
                faceIndex += 1;
                const faceName = (name || '').trim() || '表情';
                const placeholder = `[表情包${faceIndex}:${faceName}]`;
                replacements[placeholder] = origin;
                return placeholder;
            }
        );

        return { text: result, replacements };
    };

    /** 从消息 HTML 中提取远程图片，组装为 Dify 文件参数。 */
    buildDifyFiles = (input: string): DifyRemoteImageFile[] =>
        (input.match(DifyServer.IMAGE_TAG_REG) || [])
            .map((tag) => this.getRemoteImageUrlFromTag(tag))
            .filter((url) => url.length > 0)
            .map((url) => ({ type: 'image', transfer_method: 'remote_url', url }));

    /** 将 Dify 内部文件地址转换为客服侧可访问的公开地址。 */
    normalizeDifyFileUrls = (text: string): string =>
        text.split(DifyServer.DIFY_FILE_URL_PREFIX).join(DifyServer.DIFY_PUBLIC_URL_PREFIX);

    /** 过滤回复中的敏感 3387 文本，同时保留邮箱、链接和客服电话。 */
    filter3387 = (text: string): string => {
        const saved: string[] = [];
        const save = (match: string): string => {
            saved.push(match);
            return `__SAVED_${saved.length - 1}__`;
        };

        let result = text.replace(/[A-Za-z0-9._%+-]+@3387\.com/gi, save);
        result = result.replace(/https?:\/\/[^\s'"]*3387[^\s'"]*/gi, save);
        result = result.replace(/www\.[^\s'"]*3387[^\s'"]*/gi, save);
        result = result.replace(/400-969-3387/g, save);
        result = result.replace(/3387/g, '');
        saved.forEach((orig, index) => {
            result = result.replace(new RegExp(`__SAVED_${index}__`, 'g'), orig);
        });
        return result;
    };

    /** 将 Dify 纯文本安全转换为聊天消息可渲染的 HTML。 */
    textToHtml = (text: string): string => {
        if (!text) return '';

        let safe = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const markdownImages: string[] = [];
        safe = safe.replace(/!\[([^\]]*)\]\((https?:\/\/[^\s]+?)\)/gi, (_, alt, url) => {
            const placeholder = `__MD_IMAGE_${markdownImages.length}__`;
            markdownImages.push(`<img src="${url}" alt="${alt}" data-src="src" />`);
            return placeholder;
        });
        safe = safe.replace(urlReg, (rawUrl) => {
            const url = rawUrl.replace(/[)\]}.!,?，。！？、】【）]+$/g, '');
            const suffix = rawUrl.slice(url.length);
            const pathForExt = url.split(/[?#]/)[0];
            if (/\.(png|jpg|jpeg|gif|webp)$/i.test(pathForExt)) {
                return `<img src="${url}" data-src="src" />`;
            }
            return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>${suffix}`;
        });
        markdownImages.forEach((imageHtml, index) => {
            safe = safe.replace(`__MD_IMAGE_${index}__`, imageHtml);
        });
        return safe.replace(/\n/g, '<br/>');
    };

    /** 根据工作流运行 ID 获取 Dify 调试日志。 */
    getWorkflowLog = async (workflowRunId: string): Promise<DifyLogData> => {
        console.log('[Dify] getWorkflowLog workflowRunId:', workflowRunId);
        const response = await get(FAQ_WORKFLOW_LOG, {
            workflow_run_id: workflowRunId
        });
        return response.data.d as DifyLogData;
    };

    /** 获取历史被打断消息组成的后端 msg_stack，当前输入由 query 单独传递。 */
    buildMsgStack = (): string => this.pendingMsgStack.filter((item) => item.trim()).join('\n');

    /** 登记当前正在执行的工作流输入，供后续中断时保存。 */
    markWorkflowInput = (input: string): void => {
        this.activeMsgStack = input;
    };

    /** 工作流正常完成后清空已缓存的消息栈。 */
    completeWorkflow = (): void => {
        this.pendingMsgStack = [];
        this.activeMsgStack = '';
    };

    /** 记录 SOP 工作流返回的命中结果，供下一次 faq-faq 请求使用。 */
    setSopHit = (hit: unknown): void => {
        if (typeof hit === 'boolean') {
            this.sopHit = hit ? 1 : 0;
        }
    };

    /** 读取待提交给客服后端的 SOP 命中结果。 */
    getSopHit = (): 0 | 1 | undefined => this.sopHit;

    /** faq-faq 成功提交 SOP 命中结果后清除待提交状态。 */
    clearSopHit = (): void => {
        this.sopHit = undefined;
    };

    /** 工作流异常时保留当前输入，等待下一次请求补发。 */
    preserveWorkflowInput = (input: string): void => {
        if (this.activeMsgStack !== input) {
            return;
        }
        this.pendingMsgStack = [...this.pendingMsgStack, this.activeMsgStack];
        this.activeMsgStack = '';
    };

    /** 初始化或重置会话时清空消息栈和当前工作流输入。 */
    resetWorkflowState = (): void => {
        this.pendingMsgStack = [];
        this.activeMsgStack = '';
        this.sopHit = undefined;
    };

    /** 新消息发送前保存当前输入，并中断正在执行的 Dify SSE。 */
    abortWorkflow = async (): Promise<void> => {
        if (this.activeMsgStack) {
            this.pendingMsgStack = [...this.pendingMsgStack, this.activeMsgStack];
            this.activeMsgStack = '';
        }
        await abortWorkflowSSERequest();
    };
}

export default DifyServer;
